import React, { useRef, useCallback, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { useIntl } from 'react-intl';
import { Carousel } from '../../components';
import css from './SeoPage.module.css';

const RegionDisciplineSection = ({ titleOnImageTexts = [], sectionTitle, urlParts, getShortDescription }) => {
  const history = useHistory();
  const intl = useIntl();
  const sliderRef = useRef();
  const [viewportWidth, setViewportWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1440);

  // Track viewport width for responsive truncation & potential future logic
  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);



  const handleClick = useCallback(item => {
    if (item.attributes?.urlPath) {
      const routePath = `/${urlParts?.[0] || 'en-US'}${
        item.attributes.urlPath.startsWith('/') ? '' : '/'
      }${item.attributes.urlPath}`;
      history.push(routePath);
    }
  }, [urlParts, history]);

  const handlePrev = useCallback(() => {
    if (sliderRef.current) sliderRef.current.slickPrev();
  }, []);

  const handleNext = useCallback(() => {
    if (sliderRef.current) sliderRef.current.slickNext();
  }, []);

  if (!titleOnImageTexts || titleOnImageTexts.length === 0) {
    return null;
  }

  return (
    <section className={css.disciplineSlider}>
      <div className={css.headerRow}>
        <h2 className={css.disciplineHeading}>{sectionTitle}</h2>
        <div className={css.carouselArrows}>
          <button className={css.arrowBtn} onClick={handlePrev} aria-label="Previous">
            ‹
          </button>
          <button className={css.arrowBtn} onClick={handleNext} aria-label="Next">
            ›
          </button>
        </div>
      </div>
      <div className={css.carouselWrapper}>
  <Carousel
          key={`region-discipline-${titleOnImageTexts.length}-${urlParts?.[0] || 'en-US'}-${titleOnImageTexts.map(item => item.id || item.attributes?.urlPath).join('-')}`}
          ref={sliderRef}
          columns={4}
          className={css.carousel}
          contentClassName={css.carouselContent}
          showArrows={false}
          useSlider
          responsive={{
            480: { columns: 1 },
            768: { columns: 2 },
            992: { columns: 3 },
            1200: { columns: 4 }
          }}
        >
          {titleOnImageTexts.map((item, index) => {
            const { image, tag = [], section = [], urlPath } = item.attributes || {};
            

            
            // Extract discipline and region from urlPath (e.g., "/outdoor-destinations/switzerland/appenzell/hiking")
            // Format: /outdoor-destinations/country/region/discipline
            let disciplineName = '';
            let regionName = '';
            
            if (urlPath) {
              const pathParts = urlPath.split('/').filter(Boolean);
              if (pathParts.length >= 4) {
                // Last part is discipline, second-to-last is region
                disciplineName = pathParts[pathParts.length - 1]
                  .split('-')
                  .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(' ');
                regionName = pathParts[pathParts.length - 2]
                  .split('-')
                  .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(' ');
              }
            }
            
            // Fallback: Extract discipline and region from tags if urlPath parsing fails
            if (!disciplineName || !regionName) {
              const disciplineTag = tag.find(t => t.name === 'discipline');
              const regionTag = tag.find(t => t.name === 'region');
              
              disciplineName = disciplineName || (disciplineTag?.value ? 
                disciplineTag.value.charAt(0).toUpperCase() + disciplineTag.value.slice(1) : '');
              regionName = regionName || (regionTag?.value ? 
                regionTag.value.charAt(0).toUpperCase() + regionTag.value.slice(1) : '');
            }
            
            const overlayTitle = [disciplineName, regionName].filter(Boolean).join(', ');

            // Get preview text from first section paragraph (localized)
            const firstSection = section[0] || {};
            const previewText = firstSection.paragraph || '';
            

            
            // Fallback text truncation - longer for preview text
            const truncateText = (text, maxLength = 200) => {
              if (!text || text.length <= maxLength) return text;
              const truncated = text.slice(0, maxLength);
              const lastSpace = truncated.lastIndexOf(' ');
              return lastSpace > 0 ? truncated.slice(0, lastSpace) + '...' : truncated + '...';
            };
            
            // Responsive truncation lengths (aim: shorter text on smaller screens while keeping some context)
            let maxLen = 180; // desktop default
            if (viewportWidth <= 1200) maxLen = 150;
            if (viewportWidth <= 992) maxLen = 130;
            if (viewportWidth <= 768) maxLen = 110;
            if (viewportWidth <= 600) maxLen = 95;
            if (viewportWidth <= 480) maxLen = 80;
            if (viewportWidth <= 400) maxLen = 70;

            const shortPreview = getShortDescription ? getShortDescription(previewText, maxLen) : truncateText(previewText, maxLen);



            return (
              <div
                key={item.id || index}
                className={`${css.articleSlider} ${css.regionDisciplineCard} ${css.sliderCard}`}
                onClick={() => handleClick(item)}
              >
                {/* Image with overlay title */}
                <div className={css.articleImage}>
                  <img
                    className={css.regionDisciplineImage}
                    src={image?.url || '/static/imgs/placeholder.jpg'}
                    alt={overlayTitle || ''}
                    width="600"
                    height="360"
                    loading="lazy"
                    decoding="async"
                  />
                  {overlayTitle && <div className={css.articleTitle}>{overlayTitle}</div>}
                </div>

                {/* Text content */}
                <div className={css.description}>
                  <p className={css.cardDescription}>
                    {shortPreview || `Discover ${overlayTitle || 'this destination'}`}
                  </p>
                  <div className={css.readMore}>
                    {intl.formatMessage({ id: 'General.read.more', defaultMessage: 'Read more' })}
                  </div>
                </div>
              </div>
            );
          })}
        </Carousel>
      </div>
    </section>
  );
};

export default RegionDisciplineSection;
